import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  Fragment,
} from 'react';
import { Button, Form, Modal } from 'semantic-ui-react';
import readXlsxFile from 'read-excel-file';
// @ts-expect-error react-excel-workbook
import Workbook from 'react-excel-workbook';
import { QuestionAnswer } from '../../../types';

/* eslint-disable no-template-curly-in-string */
const SAMPLE_EXCEL_ROWS = [
  {
    questionId: 1001,
    userId: 5001,
    userNickname: '홍길동',
    answer:
      '안녕하세요 ${nickname}님,\n문의주신 내용 확인하였습니다.\n빠르게 도와드리겠습니다. 감사합니다.',
    questionCategoryId: 1,
    tagsJson: '["환불","배송"]',
  },
  {
    questionId: 1002,
    userId: 5002,
    userNickname: '김철수',
    answer: '${nickname}님 안녕하세요, 문의 주신 내용 확인했습니다.',
    questionCategoryId: 2,
    tagsJson: '[]',
  },
];
/* eslint-enable no-template-curly-in-string */

// BatchAnswerModal이 사용하지 않는 컬럼 인덱스들 (자리만 차지)
const UNUSED_COLUMN_INDEXES_BEFORE_ANSWER = [3, 4, 5];
const UNUSED_COLUMN_INDEXES_AFTER_ANSWER = Array.from(
  { length: 16 },
  (_, i) => i + 7,
);

type BAtchAnswerModalProps = {
  batchAnswerModalOpen: boolean;
  onAnswerQuestionList: (param: {
    values: { questionId: number; answer: QuestionAnswer }[];
  }) => Promise<void>;
  closeBatchAnswerModal: () => void;
};

const BatchAnswerModal = ({
  batchAnswerModalOpen,
  onAnswerQuestionList,
  closeBatchAnswerModal,
}: BAtchAnswerModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [excelInfoList, setExcelInfoList] = useState<
    {
      questionId: number;
      questionCategoryId: number;
      userId: number;
      userNickname: string;
      answer: string;
      tags: string[];
    }[]
  >([]);

  const readExcelFile: React.ChangeEventHandler<HTMLInputElement> = async (
    e,
  ) => {
    const data = e.target.files?.[0];
    if (!data) return;
    const rows = await readXlsxFile(data);
    console.log(data, rows);
    const excelInfo = rows
      .slice(1)
      .filter((row) => {
        // 발송할 답변 내용이 존재하는지 체크
        return !!row[6];
      })
      .map((row) => {
        const userNickname = String(row[2]);
        return {
          questionId: Number(row[0]),
          questionCategoryId: Number(row[23]),
          userId: Number(row[1]),
          userNickname,
          answer: replaceNicknameTemplate(String(row[6]), userNickname),
          tags: JSON.parse(String(row[24])),
        };
      });
    setExcelInfoList(excelInfo);
  };

  const submit = useCallback(
    async (answerType: QuestionAnswer['answerType']) => {
      const answerList = excelInfoList.map((excelInfo) => {
        const { questionId, answer, questionCategoryId, tags } = excelInfo;
        return {
          questionId,
          answer: {
            answer,
            answerImageUrls: [],
            answerType,
            tags,
            questionCategoryId,
          },
        };
      });
      setLoading(true);
      try {
        await onAnswerQuestionList({ values: answerList });
        closeBatchAnswerModal();
      } finally {
        setLoading(false);
      }
    },
    [closeBatchAnswerModal, excelInfoList, onAnswerQuestionList],
  );

  return (
    <Modal
      size="small"
      open={batchAnswerModalOpen}
      onClose={closeBatchAnswerModal}
    >
      <Modal.Header>일괄답변</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field>
            <label>
              문의id, 유저id, 유저닉네임, 답변이 포함된 엑셀 파일 업로드
            </label>
            <label>
              {'엑셀 내 ${' + 'nickname' + '} 부분은 유저닉네임으로 치환됩니다'}
            </label>
            <Workbook
              filename="일괄답변_샘플.xlsx"
              element={
                <Button basic color="black" size="small" type="button">
                  샘플 엑셀 다운로드
                </Button>
              }
            >
              <Workbook.Sheet data={SAMPLE_EXCEL_ROWS} name="sample">
                {[
                  <Workbook.Column
                    key="questionId"
                    label="문의ID"
                    value="questionId"
                  />,
                  <Workbook.Column
                    key="userId"
                    label="유저ID"
                    value="userId"
                  />,
                  <Workbook.Column
                    key="userNickname"
                    label="유저닉네임"
                    value="userNickname"
                  />,
                  ...UNUSED_COLUMN_INDEXES_BEFORE_ANSWER.map((i) => (
                    <Workbook.Column
                      key={`unused-${i}`}
                      label="(미사용)"
                      value={() => ''}
                    />
                  )),
                  <Workbook.Column
                    key="answer"
                    label="답변"
                    value="answer"
                  />,
                  ...UNUSED_COLUMN_INDEXES_AFTER_ANSWER.map((i) => (
                    <Workbook.Column
                      key={`unused-${i}`}
                      label="(미사용)"
                      value={() => ''}
                    />
                  )),
                  <Workbook.Column
                    key="questionCategoryId"
                    label="카테고리ID"
                    value="questionCategoryId"
                  />,
                  <Workbook.Column
                    key="tagsJson"
                    label="태그(JSON 배열)"
                    value="tagsJson"
                  />,
                ]}
              </Workbook.Sheet>
            </Workbook>
            <input
              style={{ marginBottom: 12 }}
              type="file"
              id="input"
              onChange={readExcelFile}
            />
          </Form.Field>
        </Form>
        {excelInfoList.length ? (
          <div style={styles.answerList}>
            {excelInfoList.map(({ userId, userNickname, answer }) => {
              return (
                <Fragment key={userId}>
                  {`유저: ${userNickname}(${userId})`}
                  <div style={styles.answer}>{answer}</div>
                </Fragment>
              );
            })}
          </div>
        ) : (
          <></>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button
          color="blue"
          loading={loading}
          content="일괄답변(푸시)"
          onClick={() => submit('PUSH')}
        />
        <Button color="black" content="닫기" onClick={closeBatchAnswerModal} />
      </Modal.Actions>
    </Modal>
  );
};

const styles: Record<string, React.CSSProperties> = {
  answerList: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    overflowY: 'scroll',
    marginBottom: 12,
    maxHeight: 500,
  },
  answer: {
    whiteSpace: 'pre-line',
    border: '1px solid black',
    marginRight: 12,
    marginBottom: 6,
  },
};

const replaceNicknameTemplate = (answer: string, nickname: string) => {
  return answer.replace(/\${nickname}/g, nickname);
};

export default BatchAnswerModal;
